import { useEffect, useState } from "react";
import { RefreshCw, Mail, Paperclip, ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import cloudflareApi, { Email as CloudflareEmail } from "../lib/cloudflare-api";
import {
  dropdownVariants,
} from "../lib/animations";

interface Email {
  id: string;
  sender: string;
  subject: string;
  body: string;
  date: string;
  read: boolean;
  attachments?: string[];
  tags?: string[];
  is_draft?: boolean;
  thread_id?: string;
}

const safeJsonParse = (str: string | null | undefined, fallback: string[] = []): string[] => {
  if (!str || typeof str !== 'string') return fallback;
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    console.error('Error parsing JSON in InboxView:', str);
    return fallback;
  }
};

function transformEmail(e: CloudflareEmail): Email {
  let dateStr: string;
  try {
    dateStr = new Date(e.timestamp).toISOString();
  } catch {
    dateStr = new Date().toISOString();
  }

  return {
    id: e.id || '',
    sender: e.sender || '',
    subject: e.subject || '(Sin asunto)',
    body: e.body_plain || e.body_html || '',
    date: dateStr,
    read: e.is_read === 1,
    attachments: e.attachments?.map(a => a.filename) || [],
    tags: safeJsonParse(e.tags),
    is_draft: e.is_draft === 1,
    thread_id: e.thread_id || undefined,
  };
}

interface InboxViewProps {
  folder: string;
  onSelectEmail: (id: string) => void;
  onRefresh: () => void;
  refreshTrigger?: number;
  onSearch?: (query: string) => void;
}

type FilterMode = 'all' | 'unread' | 'attachments';

export default function InboxView({ folder, onSelectEmail, refreshTrigger, onSearch }: InboxViewProps) {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'sender' | 'subject'>('date');
  const [sortAscending, setSortAscending] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [groupByThread, setGroupByThread] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  useEffect(() => {
    setCurrentPage(1);
    setEmails([]);
    setLoading(true);
    loadEmails(true);
  }, [folder]);

  useEffect(() => {
    if (emails.length > 0) {
      setEmails(sortEmailsLocally(emails));
    }
  }, [sortBy, sortAscending]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadEmails(false);
    }
  }, [refreshTrigger]);

  const loadEmails = async (showLoading = true) => {
    try {
      if (showLoading && emails.length === 0) {
        setLoading(true);
      }
      if (folder.startsWith("search:")) {
        const query = folder.substring(7);
        const result = await cloudflareApi.searchEmails(query);
        const transformed = result.emails.map(transformEmail);
        setEmails(sortEmailsLocally(transformed));
      } else {
        const result = await cloudflareApi.listEmails({ folder, limit: 500 });
        const transformed = result.emails.map(transformEmail);
        setEmails(sortEmailsLocally(transformed));
      }
    } catch (error) {
      console.error("Error loading emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const sortEmailsLocally = (emailList: Email[]): Email[] => {
    return [...emailList].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = new Date(b.date).getTime() - new Date(a.date).getTime();
          break;
        case 'sender':
          comparison = a.sender.localeCompare(b.sender);
          break;
        case 'subject':
          comparison = a.subject.localeCompare(b.subject);
          break;
      }
      return sortAscending ? -comparison : comparison;
    });
  };

  const handleSort = (newSortBy: 'date' | 'sender' | 'subject') => {
    if (sortBy === newSortBy) {
      setSortAscending(!sortAscending);
    } else {
      setSortBy(newSortBy);
      setSortAscending(false);
    }
    setShowSortMenu(false);
  };

  const groupEmailsByThread = () => {
    if (!groupByThread) return emails;
    const threads = new Map<string, Email[]>();
    const noThread: Email[] = [];
    emails.forEach(email => {
      if (email.thread_id) {
        if (!threads.has(email.thread_id)) threads.set(email.thread_id, []);
        threads.get(email.thread_id)!.push(email);
      } else {
        noThread.push(email);
      }
    });
    const grouped: Email[] = [];
    threads.forEach(threadEmails => grouped.push(...threadEmails));
    grouped.push(...noThread);
    return grouped;
  };

  const getFilteredEmails = () => {
    const grouped = groupEmailsByThread();
    switch (filterMode) {
      case 'unread':
        return grouped.filter(e => !e.read);
      case 'attachments':
        return grouped.filter(e => e.attachments && e.attachments.length > 0);
      default:
        return grouped;
    }
  };

  const displayEmails = getFilteredEmails();
  const totalPages = Math.ceil(displayEmails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmails = displayEmails.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Ahora";
    if (days < 1) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      return `${hours}h`;
    }
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('es', { day: 'numeric', month: 'short' });
  };

  const getSenderName = (sender: string) => {
    const match = sender.match(/^([^<]+)</);
    if (match) return match[1].trim();
    const atIndex = sender.indexOf('@');
    if (atIndex > 0) return sender.substring(0, atIndex);
    return sender;
  };

  const getSenderInitial = (sender: string) => {
    const name = getSenderName(sender);
    return name.charAt(0).toUpperCase();
  };

  const handleEmailClick = (id: string) => {
    setSelectedId(id);
    onSelectEmail(id);
    setEmails(prevEmails =>
      prevEmails.map(email =>
        email.id === id ? { ...email, read: true } : email
      )
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim() && onSearch) {
      onSearch(searchQuery);
    }
  };

  const getFolderTitle = () => {
    if (folder.startsWith("search:")) return `"${folder.substring(7)}"`;
    const titles: Record<string, string> = {
      inbox: "Recibidos",
      sent: "Enviados",
      drafts: "Borradores",
      trash: "Papelera",
      spam: "Spam",
    };
    return titles[folder] || folder;
  };

  const unreadCount = emails.filter(e => !e.read).length;

  if (loading) {
    return (
      <div className="inbox-view">
        <div style={{ padding: "16px" }}>
          {/* Search skeleton */}
          <div style={{
            height: 36,
            borderRadius: 8,
            background: "var(--border)",
            opacity: 0.4,
            marginBottom: 12,
          }} />
          {/* Header skeleton */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 100, height: 20, borderRadius: 6, background: "var(--border)", opacity: 0.4 }} />
            <div style={{ width: 40, height: 14, borderRadius: 4, background: "var(--border)", opacity: 0.3 }} />
          </div>
        </div>
        <div style={{ padding: "0 16px" }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{
              display: "flex", gap: 12, padding: "12px 0",
              borderBottom: "1px solid var(--border-light)",
              opacity: 0.5,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--border)", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ width: "50%", height: 13, borderRadius: 4, background: "var(--border)", marginBottom: 6 }} />
                <div style={{ width: "75%", height: 13, borderRadius: 4, background: "var(--border)", marginBottom: 4 }} />
                <div style={{ width: "40%", height: 11, borderRadius: 4, background: "var(--border)" }} />
              </div>
              <div style={{ width: 30, height: 11, borderRadius: 4, background: "var(--border)" }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="inbox-view">
      {/* Search bar at top */}
      <div style={{ padding: "12px 14px 0", flexShrink: 0 }}>
        <form onSubmit={handleSearch} style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 10px",
          height: 34,
          background: "var(--bg-main)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          transition: "border-color 0.15s ease",
        }}>
          <Search size={14} style={{ color: "var(--text-tertiary)", flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Buscar correos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: 13,
              outline: "none",
              padding: 0,
            }}
          />
        </form>
      </div>

      {/* Filter chips */}
      <div style={{
        display: "flex",
        gap: 6,
        padding: "10px 14px 0",
        flexShrink: 0,
      }}>
        {([
          { id: 'all' as const, label: 'Todo' },
          { id: 'unread' as const, label: 'Sin leer' },
          { id: 'attachments' as const, label: 'Adjuntos' },
        ]).map(chip => (
          <button
            key={chip.id}
            onClick={() => { setFilterMode(chip.id); setCurrentPage(1); }}
            style={{
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: filterMode === chip.id ? 600 : 400,
              color: filterMode === chip.id ? "var(--primary)" : "var(--text-tertiary)",
              background: filterMode === chip.id ? "var(--bg-selected)" : "transparent",
              border: `1px solid ${filterMode === chip.id ? "var(--primary)" : "var(--border)"}`,
              borderRadius: 6,
              cursor: "pointer",
              transition: "all 0.12s ease",
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Folder header */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        padding: "12px 14px 6px",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <h2 style={{
            fontSize: 16,
            fontWeight: 600,
            color: "var(--text-primary)",
            margin: 0,
          }}>{getFolderTitle()}</h2>
          <span style={{
            fontSize: 12,
            color: "var(--text-tertiary)",
          }}>
            {displayEmails.length}
            {unreadCount > 0 && ` · ${unreadCount} sin leer`}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Sort */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              title="Ordenar"
              style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none", borderRadius: 6, cursor: "pointer",
                color: "var(--text-tertiary)", transition: "all 0.12s ease",
              }}
            >
              <ArrowUpDown size={14} />
            </button>
            <AnimatePresence>
              {showSortMenu && (
                <motion.div
                  className="inbox-dropdown-menu"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ position: 'absolute', right: 0, top: '100%', zIndex: 10 }}
                >
                  <div className="inbox-dropdown-header">Ordenar por</div>
                  <button onClick={() => handleSort('date')}>
                    Fecha {sortBy === 'date' && (sortAscending ? '↑' : '↓')}
                  </button>
                  <button onClick={() => handleSort('sender')}>
                    Remitente {sortBy === 'sender' && (sortAscending ? '↑' : '↓')}
                  </button>
                  <button onClick={() => handleSort('subject')}>
                    Asunto {sortBy === 'subject' && (sortAscending ? '↑' : '↓')}
                  </button>
                  <div className="inbox-dropdown-divider" />
                  <button onClick={() => setGroupByThread(!groupByThread)}>
                    {groupByThread ? '✓ ' : ''}Agrupar conversaciones
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Refresh */}
          <button
            onClick={() => loadEmails(false)}
            title="Actualizar"
            style={{
              width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
              background: "transparent", border: "none", borderRadius: 6, cursor: "pointer",
              color: "var(--text-tertiary)", transition: "all 0.12s ease",
            }}
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Email list */}
      <div className="email-list">
        {displayEmails.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: "48px 24px", gap: 8, flex: 1,
          }}>
            <Mail size={40} style={{ opacity: 0.2, color: 'var(--text-muted)' }} />
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-secondary)" }}>Sin correos</div>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              {filterMode !== 'all'
                ? "No hay correos con este filtro"
                : folder.startsWith("search:")
                  ? "No se encontraron resultados"
                  : "Esta carpeta está vacía"}
            </div>
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
              key={`${folder}-${filterMode}`}
            >
              {paginatedEmails.map((email) => (
                <div
                  key={email.id}
                  className={`email-item-v2 ${email.read ? "" : "unread"} ${selectedId === email.id ? "selected" : ""}`}
                  onClick={() => handleEmailClick(email.id)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--border-light)",
                    transition: "background 0.1s ease",
                    borderLeft: !email.read ? "3px solid var(--primary)" : "3px solid transparent",
                    background: selectedId === email.id ? "var(--bg-selected)" : "transparent",
                  }}
                  onMouseEnter={(e) => { if (selectedId !== email.id) e.currentTarget.style.background = "var(--bg-hover)"; }}
                  onMouseLeave={(e) => { if (selectedId !== email.id) e.currentTarget.style.background = "transparent"; }}
                >
                  {/* Sender avatar/initial */}
                  <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: email.read ? "var(--bg-active)" : "var(--primary-light, rgba(132,204,22,0.15))",
                    color: email.read ? "var(--text-tertiary)" : "var(--primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: 2,
                  }}>
                    {getSenderInitial(email.sender)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: email.read ? 400 : 600,
                        color: email.read ? "var(--text-secondary)" : "var(--text-primary)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}>
                        {getSenderName(email.sender)}
                      </span>
                      {email.is_draft && (
                        <span style={{
                          padding: "1px 5px",
                          fontSize: 10,
                          fontWeight: 500,
                          borderRadius: 4,
                          background: "var(--primary-light, rgba(132,204,22,0.15))",
                          color: "var(--primary)",
                          flexShrink: 0,
                        }}>
                          Borrador
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: "var(--text-primary)",
                      fontWeight: email.read ? 400 : 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}>
                      {email.subject || "(Sin asunto)"}
                      {email.attachments && email.attachments.length > 0 && (
                        <Paperclip size={11} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                      )}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {email.body.substring(0, 80)}
                    </div>
                  </div>

                  {/* Date */}
                  <span style={{
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    flexShrink: 0,
                    marginTop: 3,
                  }}>
                    {formatDate(email.date)}
                  </span>
                </div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '8px 14px',
                borderTop: '1px solid var(--border)',
                fontSize: 12,
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', background: 'transparent',
                    border: '1px solid var(--border)', borderRadius: 6,
                    color: 'var(--text-secondary)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.4 : 1, fontSize: 12,
                  }}
                >
                  <ChevronLeft size={12} />
                </button>
                <span style={{ color: 'var(--text-tertiary)' }}>
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', background: 'transparent',
                    border: '1px solid var(--border)', borderRadius: 6,
                    color: 'var(--text-secondary)', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.4 : 1, fontSize: 12,
                  }}
                >
                  <ChevronRight size={12} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
